import { ClosedBanners, DismissCountBanners } from '@/constants/closedBanners';
import { getSomeDaysAgo } from '@/helpers';
import { IDatePicker } from '@/types';
import type { PayloadAction } from '@reduxjs/toolkit';
import { createSelector, createSlice } from '@reduxjs/toolkit';
import { RootState } from '../store';
export interface IDashboardState {
  titleDatePicker: string;
  datePicker: IDatePicker;
  haveClosedBanner: boolean;
  haveClosedOverQuotaBanner: boolean;
  closedUsageBannerStage: string | null;
  haveClosedRecommendedApps: boolean;
  haveClosedConstantFeaturedBanner: Partial<Record<ClosedBanners, boolean>> & {
    [key: string]: boolean | undefined;
  };
  // Generic count-based dismiss: maps banner key → number of times user closed it
  bannerCloseCount: Partial<Record<DismissCountBanners, number>>;
}

const getInitialState = (): IDashboardState => {
  return {
    datePicker: {
      start: getSomeDaysAgo(30),
      end: new Date(),
    },
    titleDatePicker: 'Current cycle',
    haveClosedBanner: false,
    haveClosedOverQuotaBanner: false,
    closedUsageBannerStage: null,
    haveClosedRecommendedApps: false,
    haveClosedConstantFeaturedBanner: {},
    bannerCloseCount: {},
  };
};

const dashboardPageSlice = createSlice({
  name: 'dashboard',
  initialState: { ...getInitialState() },
  reducers: {
    handleDatePicker: (state, action: PayloadAction<IDatePicker>) => {
      state.datePicker = action.payload;
    },
    handleTitleDatePicker: (state, action: PayloadAction<string>) => {
      state.titleDatePicker = action.payload;
    },
    handleCloseBanner: (state, action: PayloadAction<boolean>) => {
      state.haveClosedBanner = action.payload;
    },
    handleCloseOverQuotaBanner: (state, action: PayloadAction<boolean>) => {
      state.haveClosedOverQuotaBanner = action.payload;
    },
    handleCloseUsageBanner: (state, action: PayloadAction<string | null>) => {
      state.closedUsageBannerStage = action.payload;
    },
    handleCloseRecommendedApps: (state, action: PayloadAction<boolean>) => {
      state.haveClosedRecommendedApps = action.payload;
    },
    handleCloseConstantFeatureBanner: (state, action: PayloadAction<ClosedBanners | string>) => {
      state.haveClosedConstantFeaturedBanner[action.payload] = true;
    },
    incrementBannerCloseCount: (state, action: PayloadAction<DismissCountBanners>) => {
      const key = action.payload;
      state.bannerCloseCount[key] = (state.bannerCloseCount[key] ?? 0) + 1;
    },
    resetState() {
      return { ...getInitialState() };
    },
  },
});

export const datePickerSelector = createSelector(
  (state: RootState) => state.dashboard,
  (state) => state.datePicker,
);
export const titleDatePickerSelector = createSelector(
  (state: RootState) => state.dashboard,
  (state) => state.titleDatePicker,
);
export const haveClosedBannerSelector = createSelector(
  (state: RootState) => state.dashboard,
  (state) => state.haveClosedBanner,
);
export const haveClosedOverQuotaBannerSelector = createSelector(
  (state: RootState) => state.dashboard,
  (state) => state.haveClosedOverQuotaBanner,
);
export const closedUsageBannerStageSelector = createSelector(
  (state: RootState) => state.dashboard,
  (state) => state.closedUsageBannerStage ?? null,
);
export const haveClosedRecommendedAppsSelector = createSelector(
  (state: RootState) => state.dashboard,
  (state) => state.haveClosedRecommendedApps,
);

export const haveClosedConstantFeatureBannerSelector = createSelector(
  (state: RootState) => state.dashboard,
  (state) => state.haveClosedConstantFeaturedBanner,
);

// Generic selector factory: returns true when a banner has been closed >= threshold times (persisted)
export const makeBannerDismissedSelector = (key: DismissCountBanners, threshold: number) =>
  createSelector(
    (state: RootState) => state.dashboard.bannerCloseCount,
    (bannerCloseCount) => (bannerCloseCount[key] ?? 0) >= threshold,
  );

export default dashboardPageSlice;
